import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';

export type NotificationType = 'welcome' | 'generation' | 'processing' | 'feature' | 'usage' | 'error' | 'success' | 'info' | 'notification';

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  type: NotificationType;
  actionable?: boolean;
  actionText?: string;
}

// Module-level singleton to prevent multiple subscriptions
let globalChannelRef: any = null;
let globalSubscriptionInitialized = false;
let globalNotifications: AppNotification[] = [];
let globalListeners: Set<(notifications: AppNotification[]) => void> = new Set();

export const useRealTimeNotifications = () => {
  const { user } = useSecureAuthContext();
  const [notifications, setNotifications] = useState<AppNotification[]>(globalNotifications);
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);


  // Update all listeners when notifications change
  const updateNotificationsAndCount = useCallback((newNotifications: AppNotification[]) => {
    const count = newNotifications.filter(n => !n.read).length;
    console.log('Updating notifications and unread count:', { count, notifications: newNotifications });
    
    // Update global state
    globalNotifications = newNotifications;
    
    // Notify all listeners
    globalListeners.forEach(listener => listener(newNotifications));
    
    if (mountedRef.current) {
      setNotifications(newNotifications);
      setUnreadCount(count);
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data: dbNotifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }

      // Convert database notifications to app notifications
      const appNotifications: AppNotification[] = (dbNotifications || []).map(dbNotif => ({
        id: dbNotif.id,
        title: dbNotif.message?.split(':')[0] || 'Notification',
        message: dbNotif.message || 'You have a new notification',
        timestamp: new Date(dbNotif.created_at).toLocaleString(),
        read: dbNotif.is_read || false,
        type: 'notification' as NotificationType
      }));

      updateNotificationsAndCount(appNotifications);

    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    }
  }, [user?.id, updateNotificationsAndCount]);

  // Simplified real-time subscription - only for notifications table (SINGLETON)
  const subscribeToRealTimeUpdates = useCallback(() => {
    if (!user?.id || globalSubscriptionInitialized || globalChannelRef) {
      console.log('🔔 Subscription already exists, skipping...', { 
        hasUser: !!user?.id, 
        initialized: globalSubscriptionInitialized, 
        hasChannel: !!globalChannelRef 
      });
      return;
    }

    console.log('🔔 Setting up SINGLETON real-time notifications for user:', user.id);
    globalSubscriptionInitialized = true;

    try {
      // Create a simple channel for notifications only with unique timestamp
      const channelName = `notifications_singleton_${user.id}_${Date.now()}`;
      globalChannelRef = supabase.channel(channelName, {
        config: {
          broadcast: { self: true }
        }
      });

      // Only listen for notification inserts (most important)
      globalChannelRef
        .on('postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('🔔 Real-time: New notification from database:', payload);
            
            // Create notification from database payload
            const newNotification: AppNotification = {
              id: payload.new.id,
              title: payload.new.message?.split(':')[0] || 'Notification',
              message: payload.new.message || 'You have a new notification',
              timestamp: new Date(payload.new.created_at || '').toLocaleString(),
              read: payload.new.is_read || false,
              type: 'notification'
            };
            
            setNotifications(prev => {
              const updated = [newNotification, ...prev];
              updateNotificationsAndCount(updated);
              return updated;
            });
          }
        )
        .subscribe((status, err) => {
          console.log('📡 Real-time subscription status:', status);
          
          if (err) {
            console.error('❌ Subscription error:', err);
            globalSubscriptionInitialized = false;
            return;
          }
          
          if (status === 'SUBSCRIBED') {
            console.log('✅ Successfully subscribed to SINGLETON real-time notifications');
          } else if (status === 'CLOSED' || status === 'TIMED_OUT' || status === 'CHANNEL_ERROR') {
            console.log('⏰ Subscription closed/timed out/channel error:', status);
            
            // Properly cleanup the channel before retrying
            const oldChannel = globalChannelRef;
            globalChannelRef = null;
            globalSubscriptionInitialized = false;
            
            // Remove the old channel asynchronously to prevent recursion
            if (oldChannel) {
              setTimeout(() => {
                try {
                  supabase.removeChannel(oldChannel);
                  console.log('🧹 Removed old channel before retry');
                } catch (error) {
                  console.error('Error removing old channel:', error);
                }
              }, 100);
            }
            
            // Retry only for non-error cases with proper cleanup delay
            if (user?.id && status !== 'CHANNEL_ERROR') {
              setTimeout(() => {
                console.log('🔄 Retrying real-time subscription...');
                if (user?.id && !globalSubscriptionInitialized && !globalChannelRef) {
                  subscribeToRealTimeUpdates();
                }
              }, 10000);
            } else if (status === 'CHANNEL_ERROR') {
              console.warn('⚠️ Channel error occurred - subscription disabled to prevent spam');
            }
          }
        });

    } catch (error) {
      console.error('❌ Error setting up real-time subscription:', error);
      globalSubscriptionInitialized = false;
    }
  }, [user?.id, updateNotificationsAndCount]);

  // Register this component as a listener
  useEffect(() => {
    mountedRef.current = true;
    
    const listener = (newNotifications: AppNotification[]) => {
      if (mountedRef.current) {
        setNotifications(newNotifications);
        setUnreadCount(newNotifications.filter(n => !n.read).length);
      }
    };
    
    globalListeners.add(listener);
    
    return () => {
      mountedRef.current = false;
      globalListeners.delete(listener);
    };
  }, []);

  // Main effect to handle user login/logout and initialize notifications
  useEffect(() => {
    console.log('🔄 Initializing notifications for user:', user?.id);
    
    if (user?.id) {
      console.log('🔔 Setting up real-time notifications for user:', user.id);
      
      // Cleanup existing subscription if any (only on user change)
      if (globalChannelRef) {
        console.log('🧹 Cleaning up existing subscription');
        try {
          supabase.removeChannel(globalChannelRef);
        } catch (error) {
          console.error('Error cleaning up existing subscription:', error);
        }
        globalChannelRef = null;
        globalSubscriptionInitialized = false;
      }
      
      // Fetch initial notifications
      fetchNotifications();
      
      // Set up real-time subscription with a small delay to ensure user is fully loaded
      const timer = setTimeout(() => {
        subscribeToRealTimeUpdates();
      }, 1000);
      
      return () => {
        clearTimeout(timer);
      };
    } else {
      // User logged out, cleanup (only if this is the last instance)
      console.log('🔄 User logged out, cleaning up notifications');
      if (globalChannelRef && globalListeners.size <= 1) {
        try {
          supabase.removeChannel(globalChannelRef);
        } catch (error) {
          console.error('Error during logout cleanup:', error);
        }
        globalChannelRef = null;
      }
      globalSubscriptionInitialized = false;
      globalNotifications = [];
      if (mountedRef.current) {
        setNotifications([]);
        setUnreadCount(0);
      }
    }
  }, [user?.id, fetchNotifications, subscribeToRealTimeUpdates]);

  // Cleanup on unmount - only remove channel if no other instances
  useEffect(() => {
    return () => {
      if (globalChannelRef && globalListeners.size === 0) {
        console.log('🧹 Last instance unmounting: Removing real-time channel');
        try {
          supabase.removeChannel(globalChannelRef);
        } catch (error) {
          console.error('Error during cleanup:', error);
        }
        globalChannelRef = null;
        globalSubscriptionInitialized = false;
      }
    };
  }, []);

  const markAsRead = useCallback(async (id: string) => {
    try {
      // Update in database
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      // Update local state
      setNotifications(prev => {
        const updated = prev.map(n => 
          n.id === id ? { ...n, read: true } : n
        );
        updateNotificationsAndCount(updated);
        return updated;
      });

    } catch (error) {
      console.error('Error in markAsRead:', error);
    }
  }, [user?.id, updateNotificationsAndCount]);

  const markAllAsRead = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Update all unread notifications in database
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }

      // Update local state
      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, read: true }));
        updateNotificationsAndCount(updated);
        return updated;
      });

    } catch (error) {
      console.error('Error in markAllAsRead:', error);
    }
  }, [user?.id, updateNotificationsAndCount]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      // Delete from database
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', id)
        .eq('user_id', user?.id);

      if (error) {
        console.error('Error deleting notification:', error);
        return;
      }

      // Update local state
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== id);
        updateNotificationsAndCount(updated);
        return updated;
      });

    } catch (error) {
      console.error('Error in deleteNotification:', error);
    }
  }, [user?.id, updateNotificationsAndCount]);

  const clearAll = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Mark all notifications as read instead of deleting (RLS restricts deletes)
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read (clearAll):', error);
        return;
      }

      // Update local state to reflect cleared (read) status and reset unread count
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);

    } catch (error) {
      console.error('Error in clearAll:', error);
    }
  }, [user?.id]);

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refreshNotifications: fetchNotifications
  };
};